import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { usePoll } from "../hooks/usePoll";

/**
 * Data for the Trade Feed page: one paged list plus a live tail.
 *
 * Not components/InfiniteScroll.jsx, for two reasons it cannot cover:
 *
 *  1. New messages must appear at the TOP without disturbing what you are
 *     reading. InfiniteScroll only ever appends, and it wipes the list whenever
 *     `fetchParams` changes — a poll would restart the page every 15s.
 *  2. Paging has to be stable while the collection is being written to. Offset
 *     paging over a feed that grows at the head repeats rows on every page
 *     boundary, so the first load pins an upper bound (`before`) and every
 *     subsequent page is read below it. The live tail is fetched separately with
 *     `after`, above the bound.
 */

const FEED_URL = "/api/trade-feed";
// Rows per request. A dealer forwards one offer into many groups and the page
// folds those copies into one card (collapseForwards), so 24 rows could be as
// few as 6 cards. 60 rows lands nearer two dozen cards per page.
const PAGE_SIZE = 60;
/** Cap on one poll, so a long tab-away does not try to prepend thousands. */
const POLL_LIMIT = 50;
const POLL_MS = 15000;
const REQUEST_TIMEOUT_MS = 30000;

// Price re-check (Alex, 2026-10-21). The extraction pipeline writes a
// message's price up to ~30 minutes after it arrives, so a card loaded early
// said "No price" until a reload. Every minute the cards on screen that have
// no APPROVED price yet are re-asked (an extracted price can still become an
// approved one), newest first, at most PRICE_RECHECK_MAX per call.
export const PRICES_URL = "/api/trade-feed/prices";
export const PRICE_RECHECK_MS = 60000;
export const PRICE_RECHECK_MAX = 200;
const PRICE_FIELDS = [
  "badges",
  "processed",
  "prices",
  "price_count",
  "approved_count",
  "unapproved_count",
  "trade_prices",
  "extracted_prices",
  "extracted_count",
];

/**
 * The ids worth re-asking: every card, newest first. Not only the ones with
 * no approved price: a price list with some watches approved can still gain
 * prices for the rest (Alex, 2026-10-23).
 */
export const recheckIds = (items, max = PRICE_RECHECK_MAX) =>
  items
    .filter((i) => i?.id)
    .slice(0, max)
    .map((i) => i.id);

/**
 * The list with fresh price fields patched in, or the SAME array when nothing
 * changed, so an idle minute does not re-render every card.
 */
export const applyPrices = (items, fresh) => {
  let changed = false;
  const next = items.map((item) => {
    const update = fresh?.[item.id];
    if (!update) return item;
    const differs = PRICE_FIELDS.some((k) => JSON.stringify(item[k] ?? null) !== JSON.stringify(update[k] ?? null));
    if (!differs) return item;
    changed = true;
    const patched = { ...item };
    PRICE_FIELDS.forEach((k) => {
      if (k in update) patched[k] = update[k];
    });
    return patched;
  });
  return changed ? next : items;
};

/**
 * The server's code for "the cluster stopped this search at its time budget"
 * (TradeFeedController@index). A narrower term fixes it; nothing is broken.
 */
export const SEARCH_TIMEOUT_CODE = "TRADE_FEED_SEARCH_TIMEOUT";

/** `driver: "timeout"` too, so this still reads right against an older API. */
export const isSearchTimeout = (err) =>
  err?.response?.status === 503 &&
  (err?.response?.data?.code === SEARCH_TIMEOUT_CODE ||
    err?.response?.data?.driver === "timeout");

/**
 * Options shared by the first page and "load more".
 *
 * `cancelToken`, NOT `signal`: this app is on axios 0.19, which ignores an
 * AbortController signal entirely — the request runs to completion and only
 * the load-id check drops its answer. A CancelToken really aborts the XHR, so a
 * new search term frees the browser's connection instead of leaving it queued
 * behind an 8-second scan nobody is waiting for. (It cannot stop the query on
 * the server — the server's maxTimeMS does that.)
 *
 * `metadata.silent`: the page states its own failure in place. Without it the
 * global interceptor toasts "The trade feed isn't responding" AND swallows the
 * 5xx, resolving the request with no data — so the page's own timeout state
 * never rendered and the grid said "No messages match those filters" instead.
 */
const pageRequestConfig = (cancelToken) => ({
  timeout: REQUEST_TIMEOUT_MS,
  cancelToken,
  metadata: { silent: true },
});

const nowSeconds = () => Math.floor(Date.now() / 1000);

/** Newest-first, the order the API returns and the order the grid renders. */
const byNewest = (a, b) => (b.timestamp || 0) - (a.timestamp || 0);

/** Parallel time ranges a sparse search is split into. */
export const LANE_COUNT = 3;
/** Never split a range into pieces shorter than this. */
const MIN_LANE_SECONDS = 6 * 3600;

/**
 * Split what is left of a search — from a lane's cursor down to its floor —
 * into LANE_COUNT disjoint time ranges searched at once.
 *
 * Disjoint by construction (each range's floor is one second below the next
 * range's top), so two ranges can never return the same message; the merge
 * still de-duplicates by id as a second guard. The first range keeps the
 * cursor's `skip` (rows at that exact second already shown).
 */
export function splitRemaining(lane, count = LANE_COUNT) {
  const top = lane.before;
  const floor = lane.floor;
  if (floor == null || top - floor + 1 < count * MIN_LANE_SECONDS) return [lane];

  const seg = Math.floor((top - floor + 1) / count);
  return Array.from({ length: count }, (_, i) => {
    const hi = top - i * seg;
    return {
      ...lane,
      before: hi,
      skip: i === 0 ? lane.skip : 0,
      floor: i === count - 1 ? floor : hi - seg + 1,
    };
  });
}

/** TradeFeedService::PERIODS in seconds — for the ranked search's window floor. */
const WINDOW_SECONDS = { "1h": 3600, "6h": 21600, "1d": 86400, "3d": 259200, "7d": 604800, "30d": 2592000 };

/**
 * The lane that carries a search on past its default window (30 days, or the
 * 3-day ranking window): everything older than `windowFloor`, all time.
 */
export function olderLane(windowFloor, span = null) {
  return { before: windowFloor - 1, skip: 0, floor: null, span, done: false, failures: 0, allTime: true };
}

/** The lane(s) a page response leaves behind. */
export function laneAfter(lane, data, laneCount) {
  if (!data?.next) return [{ ...lane, done: true }];
  const next = {
    // Spread first so a lane's own flags (allTime) carry over.
    ...lane,
    before: data.next.before,
    skip: data.next.skip ?? 0,
    floor: lane.floor ?? data.floor ?? null,
    span: data.next.span ?? lane.span ?? null,
    done: false,
    failures: 0,
  };
  // Sparse (stopped on time, not on a full page) while still one lane: walk
  // the rest of the range in parallel from here on.
  return data.scanned_to && laneCount === 1 ? splitRemaining(next) : [next];
}

export default function useTradeFeed({
  query = "",
  countries = [],
  period = null,
  mediaAndText = false,
  hideBulk = false,
  // "Search older messages": lift the server's default window on a scanning
  // search (TradeFeedService::scanWindow). Only meaningful with a query.
  allTime = false,
  // Bumped when the store's hidden groups are saved (useHiddenGroups). Never
  // sent — the server applies the hidden set itself — it only forces a reload.
  hiddenVersion = 0,
  // Search looks in the message text only; these widen it. Sent only when on.
  includeGroup = false,
  includeName = false,
  includePhone = false,
}) {
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [driver, setDriver] = useState(null);
  // The window the server bounded a scanning search to ("30d"), or null. The
  // page prints it, so a bounded search never reads as an all-time one.
  const [searchWindow, setSearchWindow] = useState(null);
  // The second, capped set a multi-keyword search returns: recent messages
  // mentioning some of the terms but not the one that anchored the query. It is
  // context beside the answer, so it is never paged and never merged into
  // items — a "load more" that quietly started returning weaker matches would
  // be worse than not offering them.
  const [related, setRelated] = useState([]);
  // Why the feed is empty, when the server has a reason worth showing. Only
  // the first page sets it: the live tail must not be able to clear or
  // overwrite the explanation the initial load established.
  const [message, setMessage] = useState(null);

  // Monotonic load id. A response whose id is no longer current is discarded —
  // same approach as InfiniteScroll, and it covers StrictMode's double-invoke
  // as well as fast typing.
  const loadIdRef = useRef(0);
  const itemsRef = useRef([]);
  const beforeRef = useRef(null);
  // Where the next pages come from. Cursor mode: one or more "lanes", each a
  // disjoint time range with the server's keyset cursor ({before, skip}),
  // its floor and slice span. A lane is split into LANE_COUNT parallel ranges
  // as soon as a page comes back SPARSE (the server stopped on time), so a
  // rare search walks several ranges at once; dense results stay sequential,
  // where order matters and one request already returns a full page.
  // Offset mode (the ranked search sends no cursor): pinned bound + rows paged.
  const lanesRef = useRef([]);
  const offsetModeRef = useRef(false);
  const pagedRef = useRef(0);
  // The newest point still being searched, unix seconds: everything newer is
  // complete. Null when nothing is pending.
  const [scannedTo, setScannedTo] = useState(null);
  // Carrying on past the default window (30 days, or the 3-day ranking
  // window) into older messages, automatically, once the window is used up.
  // `windowFloorRef` is where the window ends; null = no window to extend
  // (browsing, or a period the reader chose).
  const windowFloorRef = useRef(null);
  const [extended, setExtended] = useState(false);
  // Rows before this index keep their order when more are merged in — the
  // ranked search's relevance order must not be re-sorted by time when older
  // matches are appended under it.
  const sortFromRef = useRef(0);
  const newestRef = useRef(null);
  // Cancel source for the current filters' page requests (first page and load
  // more). Replaced on every filter change; the previous one is cancelled.
  const cancelRef = useRef(null);

  const setList = useCallback((next) => {
    itemsRef.current = next;
    setItems(next);
    newestRef.current = next.length ? next[0].timestamp : newestRef.current;
  }, []);

  /** Merge rows in, newest first, never the same message twice. */
  const mergeRows = useCallback(
    (rows) => {
      if (!rows.length) return;
      const seen = new Set(itemsRef.current.map((i) => i.id));
      const fresh = rows.filter((i) => !seen.has(i.id));
      if (!fresh.length) return;
      const head = itemsRef.current.slice(0, sortFromRef.current);
      const tail = [...itemsRef.current.slice(sortFromRef.current), ...fresh].sort(byNewest);
      setList([...head, ...tail]);
    },
    [setList],
  );

  /**
   * When the window is used up, carry on into older messages instead of
   * stopping. Returns true when it started that older lane.
   */
  const extendPastWindow = useCallback((span = null) => {
    if (windowFloorRef.current == null) return false;
    const floor = windowFloorRef.current;
    windowFloorRef.current = null; // once
    sortFromRef.current = itemsRef.current.length;
    offsetModeRef.current = false;
    lanesRef.current = [olderLane(floor, span)];
    setExtended(true);
    return true;
  }, []);

  /** hasMore + the "still searching back to" frontier, from the lanes. */
  const syncLanes = useCallback(() => {
    if (offsetModeRef.current) return;
    let open = lanesRef.current.filter((l) => !l.done);
    if (!open.length) {
      const lastSpan = lanesRef.current.at(-1)?.span ?? null;
      if (extendPastWindow(lastSpan)) open = lanesRef.current;
    }
    setHasMore(open.length > 0);
    setScannedTo(open.length ? open[0].before : null);
  }, [extendPastWindow]);

  const filters = useCallback(
    (extra = {}) => ({
      query: query || null,
      // Omitted entirely when empty rather than sent as [], so the payload
      // stays the shape the validator expects and an unfiltered request looks
      // identical to the one this page has always sent.
      ...(countries.length ? { countries } : {}),
      period: period || null,
      ...(mediaAndText ? { media_and_text: true } : {}),
      ...(hideBulk ? { hide_bulk: true } : {}),
      ...(allTime && query ? { all_time: true } : {}),
      ...(includeGroup ? { include_group: true } : {}),
      ...(includeName ? { include_name: true } : {}),
      ...(includePhone ? { include_phone: true } : {}),
      ...extra,
    }),
    // countries.join(",") and NOT the array itself. Two effects below depend on
    // `filters`, so depending on the array's IDENTITY means any caller that
    // passes a fresh literal — including this hook's own `= []` default —
    // rebuilds the callback every render and re-triggers the initial load, i.e.
    // an endless refetch against a collection of millions. The joined string
    // changes only when the selection actually changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [query, countries.join(","), period, mediaAndText, hideBulk, allTime, hiddenVersion, includeGroup, includeName, includePhone],
  );

  // ── First page / reload on any filter change ────────────────────────────────
  useEffect(() => {
    loadIdRef.current += 1;
    const id = loadIdRef.current;

    // Pin the upper bound before the request, not from the response: an empty
    // first page would otherwise leave paging unbounded.
    beforeRef.current = nowSeconds();
    lanesRef.current = [];
    offsetModeRef.current = false;
    pagedRef.current = 0;
    windowFloorRef.current = null;
    sortFromRef.current = 0;
    setExtended(false);
    setScannedTo(null);
    newestRef.current = null;
    itemsRef.current = [];
    setItems([]);
    setRelated([]);
    setSearchWindow(null);
    setHasMore(false);
    setError(null);
    setIsLoading(true);
    // A "load more" still in flight is superseded here, and its finally() skips
    // the reset because the load id moved on. Without this the flag stayed true
    // for the rest of the session: a disabled "Loading…" button and loadMore()
    // returning early on every later search.
    setIsLoadingMore(false);

    const source = axios.CancelToken.source();
    cancelRef.current = source;

    axios
      .post(
        FEED_URL,
        {
          filters: filters({ before: beforeRef.current }),
          paging: { offset: 0, limit: PAGE_SIZE, cursor: true },
        },
        pageRequestConfig(source.token),
      )
      .then((response) => {
        if (loadIdRef.current !== id) return;
        const data = response?.data;
        const rows = data?.items ?? [];
        setList(rows);
        pagedRef.current = rows.length;

        // A search bounded by the server's DEFAULT window (not a period the
        // reader chose, not "search older") carries on past it once used up.
        const ranked = !data?.next && Boolean(data?.hasMore);
        if (query && !period && !allTime && data?.search_window) {
          windowFloorRef.current = ranked || data?.floor == null
            ? beforeRef.current - (WINDOW_SECONDS[data.search_window] ?? 0)
            : data.floor;
        }

        if (data?.next) {
          lanesRef.current = laneAfter(
            { before: beforeRef.current, skip: 0, floor: data.floor ?? null, span: null, done: false, failures: 0 },
            data,
            1,
          );
          syncLanes();
        } else if (ranked) {
          // No cursor but more to come: the ranked search, paged by offset.
          offsetModeRef.current = true;
          setHasMore(true);
        } else {
          // The window answered in one page: go straight on to older ones.
          lanesRef.current = [];
          syncLanes();
        }
        setDriver(response?.data?.driver ?? null);
        setSearchWindow(response?.data?.search_window ?? null);
        setRelated(response?.data?.related ?? []);
        setMessage(response?.data?.message ?? null);
        setUpdatedAt(Date.now());
      })
      .catch((err) => {
        if (loadIdRef.current !== id || axios.isCancel(err)) return;
        setError(err);
        setHasMore(false);
      })
      .finally(() => {
        if (loadIdRef.current === id) setIsLoading(false);
      });

    return () => {
      // Invalidate this load so a late response cannot land on the next filter,
      // and abort it (and any load-more under it) so it stops holding a
      // connection.
      loadIdRef.current += 1;
      source.cancel("Trade Feed filters changed");
    };
  }, [filters, setList, syncLanes, query, period, allTime]);

  // ── Next page ──────────────────────────────────────────────────────────────
  const loadMore = useCallback(() => {
    if (isLoading || isLoadingMore || !hasMore) return;

    const id = loadIdRef.current;
    const config = pageRequestConfig(cancelRef.current?.token);
    setIsLoadingMore(true);

    // Offset mode (ranked search): one request below the pinned bound. The
    // offset counts rows paged — never itemsRef.length, which also counts rows
    // the live tail prepended.
    if (offsetModeRef.current) {
      axios
        .post(
          FEED_URL,
          {
            filters: filters({ before: beforeRef.current }),
            paging: { offset: pagedRef.current, limit: PAGE_SIZE, cursor: true },
          },
          config,
        )
        .then((response) => {
          if (loadIdRef.current !== id) return;
          const rows = response?.data?.items ?? [];
          pagedRef.current += rows.length;
          const seen = new Set(itemsRef.current.map((i) => i.id));
          setList([...itemsRef.current, ...rows.filter((i) => !seen.has(i.id))]);
          if (response?.data?.hasMore) {
            setHasMore(true);
          } else if (extendPastWindow()) {
            // Ranked window used up: older matches follow, newest first.
            syncLanes();
          } else {
            setHasMore(false);
          }
        })
        .catch((err) => {
          if (loadIdRef.current !== id || axios.isCancel(err)) return;
          setError(err);
          setHasMore(false);
        })
        .finally(() => {
          if (loadIdRef.current === id) setIsLoadingMore(false);
        });
      return;
    }

    // Cursor mode: every open lane at once. Each request is one lane's own
    // time range, so they run in parallel without overlapping.
    const open = lanesRef.current.filter((l) => !l.done);
    let lastError = null;

    Promise.all(
      open.map((lane) =>
        axios
          .post(
            FEED_URL,
            {
              filters: filters({
                before: lane.before,
                ...(lane.floor != null ? { floor: lane.floor } : {}),
                // Past the default window: no 30-day bound for this range.
                ...(lane.allTime ? { all_time: true } : {}),
              }),
              paging: {
                offset: lane.skip,
                limit: PAGE_SIZE,
                cursor: true,
                ...(lane.span ? { span: lane.span } : {}),
              },
            },
            config,
          )
          .then((response) => {
            if (loadIdRef.current !== id) return;
            // A 5xx resolves undefined through the global interceptor.
            if (!response) throw new Error("Trade Feed page failed");
            mergeRows(response.data?.items ?? []);
            const replaced = laneAfter(lane, response.data, lanesRef.current.length);
            lanesRef.current = lanesRef.current.flatMap((l) => (l === lane ? replaced : [l]));
          })
          .catch((err) => {
            if (loadIdRef.current !== id || axios.isCancel(err)) return;
            lastError = err;
            // Retried on the next round; a range that fails twice is given up
            // on so one bad range cannot stall the rest.
            lanesRef.current = lanesRef.current.map((l) =>
              l === lane ? { ...l, failures: (l.failures ?? 0) + 1, done: (l.failures ?? 0) + 1 >= 2 } : l,
            );
          }),
      ),
    ).finally(() => {
      if (loadIdRef.current !== id) return;
      syncLanes();
      if (lastError && !itemsRef.current.length && lanesRef.current.every((l) => l.done)) {
        setError(lastError);
      }
      setIsLoadingMore(false);
    });
  }, [filters, hasMore, isLoading, isLoadingMore, setList, mergeRows, syncLanes, extendPastWindow]);

  // ── Live tail ──────────────────────────────────────────────────────────────
  // Only while browsing, not while searching: search results are ranked by
  // relevance, so splicing rows in by arrival time would reorder the list under
  // the reader for no gain.
  const isLive = query.trim() === "";

  // usePoll (M4.1): same 15 s chain. A hidden tab polls nothing — it would
  // only queue prepends nobody is reading — and asks once on return, catching
  // up via `after`. A failed poll backs off instead of retrying every 15 s,
  // and 401/403/404 stop it. Never surfaced, as before.
  const tailPoll = useCallback(async () => {
    const id = loadIdRef.current;
    // With no rows yet (an empty feed, or a filter that matched nothing) the
    // watermark is the pinned load time, so an empty page still goes live
    // instead of waiting for a row it will never get.
    const after = newestRef.current ?? beforeRef.current;

    const response = await axios.post(
      FEED_URL,
      { filters: filters(), after, limit: POLL_LIMIT },
      { timeout: REQUEST_TIMEOUT_MS },
    );
    // A 5xx / dropped connection RESOLVES undefined (global interceptor).
    // That is a failed poll, not an empty one: it must not clear a real page
    // error or claim "updated just now".
    if (!response) throw new Error("Trade Feed live poll failed");
    if (loadIdRef.current !== id) return;
    const fresh = response?.data?.items ?? [];
    if (fresh.length) {
      const seen = new Set(itemsRef.current.map((i) => i.id));
      const added = fresh.filter((i) => !seen.has(i.id));
      if (added.length) {
        setList([...added, ...itemsRef.current].sort(byNewest));
      }
    }
    // A poll that succeeds clears an earlier failure. Without this the
    // error stayed on screen for the rest of the session even though the
    // feed was answering again — and, worse, sat next to a live
    // "updated Ns ago" heartbeat saying the opposite (seen on staging
    // 2026-09-07: the first page 500'd, then a poll returning zero new
    // rows succeeded).
    setError(null);
    setUpdatedAt(Date.now());
  }, [filters, setList]);

  usePoll(tailPoll, { interval: POLL_MS, enabled: isLive });

  // ── Price re-check ───────────────────────────────────────────────────────
  // Searching or browsing alike: patching a card's price never reorders the
  // list. A hidden tab asks nothing; a failed call waits for the next minute.
  // usePoll (M4.1): same 1-minute chain; a failed call backs off instead of
  // retrying every minute, 401/403/404 stop it, a hidden tab asks nothing.
  const priceRecheck = useCallback(async () => {
    const ids = recheckIds(itemsRef.current);
    if (!ids.length) return;

    const id = loadIdRef.current;
    const response = await axios.post(PRICES_URL, { ids, query: query.trim() || undefined }, { timeout: REQUEST_TIMEOUT_MS });
    // A 5xx / dropped connection RESOLVES undefined (global interceptor): a failed call.
    if (!response) throw new Error("Trade Feed price re-check failed");
    if (loadIdRef.current !== id) return;
    const next = applyPrices(itemsRef.current, response?.data?.items);
    if (next !== itemsRef.current) setList(next);
  }, [query, setList]);

  usePoll(priceRecheck, { interval: PRICE_RECHECK_MS });

  return {
    items,
    isLoading,
    isLoadingMore,
    hasMore,
    error,
    loadMore,
    updatedAt,
    isLive,
    driver,
    searchWindow,
    related,
    message,
    scannedTo,
    extended,
  };
}
